import { Formik } from "formik";
import { object, string } from "yup";

import { Link as DSLink, Heading } from "@icg360/design-system";

import { Body } from "components/common/body";
import { MSSLink } from "components/common/link";
import { FormFooterLinks } from "components/shared/form-footer-links";
import { CONFIG } from "config";
import { HTTP_BAD_REQUEST, validatePasswordFormik } from "consts";
import { KeystoneApiClient } from "data/keystone-api-client";
import { isError } from "data/ss-error";
import { logException, trackEvent } from "utils";

import { CeremonyRegistrationForm } from "./ceremony-registration-form";
import styles from "./ceremony-registration.module.scss";

const registrationSchema = object().shape({
  policyNumber: string()
    .required("Please enter your 10 to 12 digit policy number.")
    .matches(
      /^[a-zA-Z0-9]{10}([a-zA-Z0-9])?([a-zA-Z0-9])?$/,
      "Please enter your 10 to 12 digit policy number."
    ),
  zipCode: string()
    .required("Please enter a valid 5-digit property zip code.")
    .matches(
      /^[0-9]{5}(-[0-9]{4})?$/,
      "Please enter a valid 5-digit property zip code."
    ),
  firstName: string().required("Please enter a first name."),
  lastName: string().required("Please enter a last name."),
  email: string()
    .required("Please enter an email address.")
    .email("Please enter a valid email address."),
});

const initialValues = {
  policyNumber: "",
  zipCode: "",
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  passwordConfirmation: "",
};

export type CeremonyRegistrationSchema = typeof initialValues;

type CeremonyRegistrationProps = {
  firstName: string;
  uuid?: string;
};

export const CeremonyRegistration = ({
  firstName: userFirstName,
  uuid,
}: CeremonyRegistrationProps) => {
  const keystoneApi = new KeystoneApiClient();

  const handleSubmit = async (
    { policyNumber, firstName, lastName, zipCode, email, password },
    { setSubmitting, setStatus }
  ) => {
    try {
      setSubmitting(true);
      setStatus(null);
      const result = await keystoneApi.registerLegacyUser({
        emailVerified: false,
        policyList: policyNumber,
        email,
        firstName,
        lastName,
        password,
        zipCode,
      });

      if (isError(result)) {
        trackEvent("Error Displayed (Geico Registration)", {
          accountEmail: email,
          errorCode: result.name,
          errorMessage: result.message,
          policyNumber,
          zipCode,
        });
        setStatus({
          type: "error",
          message:
            result.message ||
            "There was an error while attempting to create this account",
        });
        setSubmitting(false);
        return;
      }

      const samlBridgeResponse = await fetch(
        `${CONFIG.KEYSTONE_PROXY_HREF}/api/saml-bridge`,
        {
          body: JSON.stringify({
            policyNumber,
            email,
            password,
            uuid,
            origin: "register",
          }),
          headers: { "content-type": "application/json" },
          method: "POST",
          redirect: "follow",
          credentials: "include",
        }
      );

      if (samlBridgeResponse.status >= HTTP_BAD_REQUEST) {
        trackEvent("Error Displayed (Geico Registration)", {
          accountEmail: email,
          errorCode: samlBridgeResponse.status,
          errorMessage: `${samlBridgeResponse.statusText} – type:${
            samlBridgeResponse.type || ""
          }`,
          policyNumber,
          zipCode,
        });
        throw new Error(
          "An error occurred, and we were unable to redirect you."
        );
      } else {
        setStatus({
          type: "success",
          message: "Account successfully registered.",
        });
        const body = await samlBridgeResponse.text();
        trackEvent("GEICO: Registration", {
          "Destination URL": body,
        });
        window.location.assign(body);
      }

      setSubmitting(false);
    } catch (err) {
      logException(err);
      setStatus({ type: "error", message: err.message });
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.ceremonyRegister}>
      <Heading size="md" className={styles.formHeadline}>
        Welcome
        {userFirstName ? `, ${userFirstName}` : ""}!
      </Heading>

      <Body className={styles.infoHeadline}>
        Our online customer care center allows you to access your policy any
        time, anywhere.
      </Body>

      <Heading size="xs" className={styles.capitalizedHeader}>
        Choose a policy to register
      </Heading>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={registrationSchema}
        validate={validatePasswordFormik}
      >
        <CeremonyRegistrationForm />
      </Formik>

      <FormFooterLinks>
        <Body small>
          By signing up, you agree to our{" "}
          <DSLink
            href="https://www.sagesure.com/terms-and-conditions/"
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms and Conditions
          </DSLink>{" "}
          and{" "}
          <DSLink
            href="https://www.sagesure.com/privacy-policy/"
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy Policy
          </DSLink>
          .
        </Body>
        <Body small>
          Already have an account? <MSSLink to="/">Log in</MSSLink>.
        </Body>
      </FormFooterLinks>
    </div>
  );
};
